import { BalanceData, GEAR_STAT_IDS, GearStat, GearTarget } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import type { GearAffix, GearItem, GearOwner } from "./gear";

/**
 * How a gear stat counts on an owner (DESIGN §9.4; Kevin 2026-10-24, gear.json `retarget`): the tower's affinity, and
 * the stat an item's primary or affix counts as there — itself, or on a damageless tower (Frost, Beacon, Hex Totem) that
 * cannot use it, that tower's affinity stat at the base exchange rate. Saved items never change; this is how they are read.
 */

/** The stat a tower archetype answers best to (§9.4 affinity), −1 for the base or none. */
export function affinityOf(balance: BalanceData, owner: GearOwner): number {
  if (owner.target !== GearTarget.Tower) return -1;
  const t = balance.towers[owner.towerIndex];
  return t === undefined ? -1 : (balance.gear.affinity.get(t.id) ?? -1);
}

/**
 * The stat `stat` counts as on `owner`: itself, or on a tower that cannot use it (gear.json `retarget`: a Hex Totem's
 * damage, a Beacon's fire rate) that tower's affinity stat, so no gear is dead weight there (Kevin 2026-10-24).
 */
export function statOn(balance: BalanceData, owner: GearOwner, stat: GearStat): GearStat {
  if (owner.target !== GearTarget.Tower) return stat;
  const t = balance.towers[owner.towerIndex];
  const aff = affinityOf(balance, owner);
  return t !== undefined && aff >= 0 && (balance.gear.retarget.get(t.id)?.includes(stat) ?? false) ? aff : stat;
}

/** The stat an item's primary counts as on this owner (`statOn`). */
export const primaryStatOn = (balance: BalanceData, item: GearItem, owner: GearOwner): GearStat => statOn(balance, owner, item.primaryStat);

/** A value of stat `from` in stat `to` at the base exchange rate (`rates[to] / rates[from]`); itself when they match. */
export function exchange(v: Fix64, from: GearStat, to: GearStat, rates: readonly Fix64[]): Fix64 {
  return from === to || rates[from].isZero() ? v : v.mul(rates[to]).div(rates[from]);
}

/** An affix on this owner: the stat it counts as there and its value (at the affix values' exchange rate). */
export function affixOn(balance: BalanceData, affix: GearAffix, owner: GearOwner): GearAffix {
  const s = statOn(balance, owner, affix.stat);
  if (s === affix.stat) return affix;
  return { stat: s, value: exchange(affix.value, affix.stat, s, affixRates(balance)) };
}

/** Per balance bundle: each stat's affix value, the exchange rate between affixes. */
const AFFIX_RATES = new WeakMap<BalanceData, readonly Fix64[]>();
function affixRates(balance: BalanceData): readonly Fix64[] {
  let r = AFFIX_RATES.get(balance);
  if (r === undefined) { r = GEAR_STAT_IDS.map((_, i) => balance.gear.affixes.find((d) => d.stat === i)?.value ?? Fix64.Zero); AFFIX_RATES.set(balance, r); }
  return r;
}

